import { createSelector } from "reselect";

import type {
    ColumnItem,
    SimilarwebFieldsConfig,
} from "app/pages/sales-intelligence/common-components/export-table/types";
import type { RootState } from "single-spa/store/types";

import { exportTypes, exportTypeToRecordName } from "../constants";
import type { ExportName, ExportSetting, ExportSettingRequest, ExportSettingResponse } from "../store/type";

import { getExportSettingTitle } from "./utils";
import { getUniqueFieldName } from "app/pages/sales-intelligence/common-components/export-table/utils";

export function enrichExportSettings(
    exportSettings: ExportSettingResponse[],
    config: SimilarwebFieldsConfig,
): ExportSetting[] {
    const tableData: { field: string }[] = [];

    return exportSettings.map((setting) => {
        const { metadata, sourceType, externalField, overwrite, type, forExport, forEnrichment } = setting;
        const isRegularSwField = Boolean(metadata && config[metadata]);
        const { fieldName } = isRegularSwField ? getUniqueFieldName(metadata, tableData) : { fieldName: metadata };
        tableData.push({ field: fieldName });

        return {
            metadata,
            title: getExportSettingTitle(metadata, sourceType, config),
            externalField: externalField || "",
            overwrite,
            field: fieldName,
            example: config[metadata]?.example || "",
            sourceType,
            type,
            forExport,
            forEnrichment,
        };
    });
}

export function createExportSettingsDto(settings: readonly ExportSetting[]): readonly ExportSettingRequest[] {
    return settings.map(({ overwrite, externalField, metadata = "", sourceType, forExport, forEnrichment }) => ({
        overwrite,
        metadata,
        externalField,
        sourceType,
        forExport,
        forEnrichment,
    }));
}

export function groupByRecordType(settings: ExportSettingResponse[]) {
    return settings.reduce<Record<ExportName, ExportSettingResponse[]>>(
        (result, setting) => {
            if (!exportTypes.includes(setting.type)) {
                return result;
            }
            const recordName = exportTypeToRecordName[setting.type];
            result[recordName].push(setting);
            return result;
        },
        { accounts: [], contacts: [], leads: [], companyAsLeads: [] },
    );
}

export const countMissingRequiredColumns = (requiredColumns: string[], mapped: string[]) =>
    requiredColumns.filter((required) => !mapped.includes(required)).length;

export const makeSelectRequiredColumns = (selectColumns: (state: RootState) => ColumnItem[]) =>
    createSelector(selectColumns, (columns) =>
        columns.filter((column) => column.isRequired && column.isEditable).map((column) => column.name),
    );

export const makeSelectMappedFields = (selectMappings: (state: RootState) => ExportSetting[]) =>
    createSelector(selectMappings, (mappings) => mappings.map((mapping) => mapping.externalField));

export const deleteSetting = (
    settings: ExportSetting[],
    settingToDelete: ExportSetting,
): { updatedSettings: ExportSetting[]; deletedSetting: ExportSetting | null } => {
    const compareKey = settingToDelete.field ? "field" : "metadata";

    if (!settingToDelete[compareKey]) {
        return { updatedSettings: settings, deletedSetting: null };
    }

    const deletedSetting = settings.find((setting) => setting[compareKey] === settingToDelete[compareKey]) || null;

    const updatedSettings = settings.filter((setting) => setting[compareKey] !== settingToDelete[compareKey]);

    return { updatedSettings, deletedSetting };
};
